import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  RefreshCw,
  Search,
  Upload,
  Users,
} from 'lucide-react';
import { api, extractErrorMessage } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { ImportPersonnelsModal } from '../components/personnels/ImportPersonnelsModal';

/**
 * Page `/personnels` — annuaire des personnels (US-PERS-01 + 02).
 *
 *  - KPIs (total / actifs / connectés < 30j / inactifs)
 *  - Liste paginée + filtres (rôle, statut, recherche)
 *  - Tri par colonnes
 *  - Boutons Export Excel (US-PERS-04) + Import (admin only)
 *  - Click ligne → /personnels/:id (fiche 360°, US-PERS-03)
 */

interface PersonnelRow {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  role_id: string | null;
  service_id: string | null;
  is_active: boolean;
  locked_until: string | null;
  last_login_at: string | null;
  created_at: string;
}

interface RoleOption {
  id: string;
  name: string;
}

interface Pagination {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

interface Stats {
  total: number;
  active: number;
  inactive: number;
  recently_active_30d: number;
  by_role: Array<{ role_id: string | null; role_name: string; count: number }>;
}

type SortBy = 'email' | 'first_name' | 'last_name' | 'created_at' | 'last_login_at';
type SortOrder = 'asc' | 'desc';
type StatusFilter = 'all' | 'active' | 'inactive' | 'locked';

export function PersonnelsListPage() {
  const { can } = usePermissions();
  const canImport = can('administration', 'manage');

  const [items, setItems] = useState<PersonnelRow[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [roleFilter, setRoleFilter] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('last_name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [page, setPage] = useState(1);

  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, roleFilter, sortBy, sortOrder]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statusFilter, roleFilter, sortBy, sortOrder, page]);

  useEffect(() => {
    loadRoles();
    loadStats();
  }, []);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const params = buildQueryParams();
      params.set('page', String(page));
      params.set('page_size', '25');
      const res = await api.get<{ data: PersonnelRow[]; pagination: Pagination }>(
        `/personnels?${params}`,
      );
      setItems(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    try {
      const res = await api.get<{ data: Stats }>('/personnels/stats');
      setStats(res.data.data);
    } catch {
      // Silencieux.
    }
  }

  async function loadRoles() {
    try {
      const res = await api.get<{ data: RoleOption[] }>('/admin/roles');
      setRoles(res.data.data);
    } catch {
      // Pas grave si l'user n'a pas accès aux rôles — la liste reste vide.
    }
  }

  function buildQueryParams(): URLSearchParams {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (roleFilter) params.set('role_id', roleFilter);
    params.set('sort_by', sortBy);
    params.set('sort_order', sortOrder);
    return params;
  }

  function toggleSort(column: SortBy) {
    if (sortBy === column) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  }

  async function handleExport() {
    setExporting(true);
    setErrorMessage(null);
    try {
      const params = buildQueryParams();
      const res = await api.get(`/personnels/export.xlsx?${params}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `personnels-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setExporting(false);
    }
  }

  const rolesById = useMemo(() => new Map(roles.map((r) => [r.id, r])), [roles]);

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <h1 className="text-2xl font-bold text-gray-900 flex items-center">
        <Users className="w-7 h-7 mr-2 text-primary-600" />
        Annuaire des personnels
      </h1>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Total" value={stats?.total ?? 0} tone="default" />
        <Kpi label="Actifs" value={stats?.active ?? 0} tone="green" />
        <Kpi label="Inactifs" value={stats?.inactive ?? 0} tone="gray" />
        <Kpi label="Connectés < 30j" value={stats?.recently_active_30d ?? 0} tone="primary" />
      </div>

      {/* Répartition par rôle */}
      {stats && stats.by_role.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">Répartition par rôle</h2>
          <div className="flex flex-wrap gap-2">
            {stats.by_role.map((r) => (
              <span
                key={r.role_id ?? 'none'}
                className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs"
              >
                {r.role_name}
                <span className="ml-2 font-semibold text-primary-600">{r.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Filtres + actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 flex flex-wrap items-center gap-3 border-b border-gray-200">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              placeholder="Rechercher (email, prénom, nom)…"
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">Tous les statuts</option>
            <option value="active">Actifs</option>
            <option value="inactive">Désactivés</option>
            <option value="locked">Verrouillés</option>
          </select>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Tous les rôles</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => {
              refresh();
              loadStats();
            }}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            title="Rafraîchir"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-1.5" />
            )}
            Export Excel
          </button>
          {canImport && (
            <button
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600"
            >
              <Upload className="w-4 h-4 mr-2" />
              Importer
            </button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableTh
                  column="last_name"
                  label="Nom"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={toggleSort}
                />
                <SortableTh
                  column="email"
                  label="Email"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={toggleSort}
                />
                <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Rôle
                </th>
                <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Statut
                </th>
                <SortableTh
                  column="last_login_at"
                  label="Dernière connexion"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={toggleSort}
                  className="hidden xl:table-cell"
                />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                    Aucun personnel ne correspond aux filtres.
                  </td>
                </tr>
              ) : (
                items.map((u) => {
                  const role = rolesById.get(u.role_id ?? '');
                  const locked = u.locked_until && new Date(u.locked_until) > new Date();
                  return (
                    <tr key={u.id} className="hover:bg-gray-50 cursor-pointer">
                      <td className="px-4 py-3">
                        <Link to={`/personnels/${u.id}`} className="block">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                              {(u.first_name[0] ?? '') + (u.last_name[0] ?? '')}
                            </div>
                            <span className="text-sm font-medium text-gray-900">
                              {u.last_name} {u.first_name}
                            </span>
                          </div>
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/personnels/${u.id}`}
                          className="text-sm text-gray-700 hover:text-primary-600"
                        >
                          {u.email}
                        </Link>
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-700">
                        {role?.name ?? '—'}
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3">
                        <StatusBadge active={u.is_active} locked={Boolean(locked)} />
                      </td>
                      <td className="hidden xl:table-cell px-4 py-3 text-xs text-gray-500">
                        {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'jamais'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm">
            <span className="text-gray-600">
              {pagination.total} personnel(s) — page {pagination.page} / {pagination.total_pages}
            </span>
            <div className="flex items-center space-x-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
              </button>
              <button
                disabled={page >= pagination.total_pages}
                onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Suivant <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>

      {importOpen && (
        <ImportPersonnelsModal
          onClose={() => setImportOpen(false)}
          onCompleted={() => {
            setImportOpen(false);
            refresh();
            loadStats();
          }}
        />
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'default' | 'primary' | 'green' | 'gray';
}) {
  const tones: Record<typeof tone, string> = {
    default: 'bg-white text-gray-900',
    primary: 'bg-primary-50 text-primary-700',
    green: 'bg-green-50 text-green-700',
    gray: 'bg-gray-100 text-gray-700',
  };
  return (
    <div className={`rounded-xl border border-gray-200 p-4 shadow-sm ${tones[tone]}`}>
      <p className="text-xs uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}

function SortableTh({
  column,
  label,
  sortBy,
  sortOrder,
  onSort,
  className,
}: {
  column: SortBy;
  label: string;
  sortBy: SortBy;
  sortOrder: SortOrder;
  onSort(c: SortBy): void;
  className?: string;
}) {
  const active = sortBy === column;
  return (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none ${className ?? ''}`}
      onClick={() => onSort(column)}
    >
      <span className="inline-flex items-center">
        {label}
        {active &&
          (sortOrder === 'asc' ? (
            <ArrowUp className="w-3 h-3 ml-1" />
          ) : (
            <ArrowDown className="w-3 h-3 ml-1" />
          ))}
      </span>
    </th>
  );
}

function StatusBadge({ active, locked }: { active: boolean; locked: boolean }) {
  if (locked) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        Verrouillé
      </span>
    );
  }
  if (active) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Actif
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
      Désactivé
    </span>
  );
}
